import Header from "@/components/layout/Header.tsx";
import { YOUTUBE_TERMS_URL, GOOGLE_PRIVACY_URL, GOOGLE_PERMISSIONS_URL } from "@/lib/data-privacy.ts";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  User,
  CreditCard,
  Youtube,
  ExternalLink,
  Trash2,
  Plus,
  Shield,
  Mail,
  Music2,
  Users,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Input } from "@/components/ui/Input.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { useAuth } from "@/hooks/useAuth.ts";
import { billingApi } from "@/api/billing.ts";
import { creditsApi } from "@/api/credits.ts";
import { apiGet, apiPost, apiDelete } from "@/api/client.ts";
import { publishingApi } from "@/api/videos.ts";
import { connectInPopup, connectErrorText, takeKeptConnectResult, CONNECT_MESSAGE, type ConnectResult } from "@/lib/connect-popup.ts";
import { PLANS, type PlanTier } from "@vms/tubeforge-shared";
import { reconnectPrompt, disconnectWords } from "@/lib/channel-status.ts";

interface YouTubeChannel {
  id: string;
  youtubeChannelId: string;
  channelTitle: string | null;
  connectedAt: string;
  /** (tf3-34b) needs_reconnect / expired → Settings asks to reconnect */
  status?: string; statusReason?: string | null;
}

const channelsApi = {
  list: () => apiGet<{ channels: YouTubeChannel[] }>("/youtube/channels"),
  // (tf3-34) connecting runs through @vms/publishing's flow, as TikTok does (lib/connect-popup.ts); the old route is a 410
  connect: () => connectInPopup("youtube", publishingApi.connect),
  disconnect: (id: string) => apiDelete<DisconnectAnswer>(`/youtube/channels/${id}`),
};

interface TikTokChannel {
  id: string;
  tiktokOpenId: string;
  displayName: string | null;
  avatarUrl: string | null;
  connectedAt: string;
  status?: string; statusReason?: string | null;
}
/** (tf3-34b) what a Disconnect did (the package's full disconnect) */
type DisconnectAnswer = { ok: boolean; revoked?: boolean; revokeError?: string | null };

const tiktokApi = {
  list: () => apiGet<{ channels: TikTokChannel[] }>("/tiktok/channels"),
  // (tf3-26a) connecting runs through @vms/publishing's flow (the package's redirect URI; lib/connect-popup.ts)
  connect: () => connectInPopup("tiktok", publishingApi.connect),
  disconnect: (id: string) => apiDelete<DisconnectAnswer>(`/tiktok/channels/${id}`),
};

// (tf3-34b; Part B) Instagram, on Instagram Login, through the package's flow as TikTok and YouTube
interface InstagramChannel { id: string; instagramUserId: string; username: string | null; avatarUrl: string | null; connectedAt: string; status?: string; statusReason?: string | null }
const instagramApi = {
  list: () => apiGet<{ channels: InstagramChannel[] }>("/instagram/channels"),
  connect: () => connectInPopup("instagram", publishingApi.connect),
  disconnect: (id: string) => apiDelete<DisconnectAnswer>(`/instagram/channels/${id}`),
};
/** (tf3-34b) which platforms this TubeForge can connect (their app keys are set) */
const socialApi = { enabled: () => apiGet<{ youtube: boolean; tiktok: boolean; instagram: boolean }>("/social/enabled") };

export function SettingsPage() {
  const { user, logout } = useAuth();

  const { data: balanceData } = useQuery({
    queryKey: ["credits", "balance"],
    queryFn: () => creditsApi.balance(),
  });

  const { data: enabled } = useQuery({ queryKey: ["social", "enabled"], queryFn: () => socialApi.enabled(), staleTime: 5 * 60_000 });
  const plan = (user?.currentPlan ?? "free") as PlanTier;
  const planLimits = PLANS[plan];
  const videoCredits = Math.floor((balanceData?.balance ?? 0) / 100);

  return (
    <div className="mx-auto max-w-4xl p-8">
      <Header title="Settings" subtitle="Manage your account and connected channels" />
      <h1 className="mb-2 text-3xl font-bold text-fg">Settings</h1>
      <p className="mb-8 text-fgMuted">Manage your account, subscription, and connected channels.</p>

      {/* Account */}
      <AccountSection user={user} onLogout={logout} />

      {/* Plan & credits */}
      <PlanSection plan={plan} planLimits={planLimits} videoCredits={videoCredits} />

      {/* Team */}
      {planLimits.teamSeats > 1 && <TeamSection />}

      {/* YouTube channels */}
      <YouTubeSection plan={plan} planLimits={planLimits} />

      {/* TikTok accounts */}
      <TikTokSection plan={plan} planLimits={planLimits} />

      {/* (tf3-34b) Instagram accounts, when this TubeForge has Instagram's app keys */}
      {enabled?.instagram && <InstagramSection planLimits={planLimits} />}

      {/* Danger zone */}
      <DangerSection onLogout={logout} />
    </div>
  );
}

function AccountSection({ user, onLogout: _onLogout }: { user: ReturnType<typeof useAuth>["user"]; onLogout: () => void }) {
  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500/15 text-brand-500">
            <User className="h-5 w-5" />
          </div>
          <div>
            <CardTitle>Account</CardTitle>
            <CardDescription>Your account details</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between rounded-lg border border-border bg-surface2/30 px-4 py-3">
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-fgMuted" />
              <div>
                <p className="text-sm font-medium text-fg">{user?.email ?? "—"}</p>
                <p className="text-xs text-fgMuted">Email address</p>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border bg-surface2/30 px-4 py-3">
            <div className="flex items-center gap-3">
              <Shield className="h-4 w-4 text-fgMuted" />
              <div>
                <p className="text-sm font-medium text-fg capitalize">{user?.currentPlan ?? "No plan"}</p>
                <p className="text-xs text-fgMuted">Current plan</p>
              </div>
            </div>
            <Badge variant="info" className="capitalize">{user?.currentPlan ?? "free"}</Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PlanSection({
  plan: _plan,
  planLimits,
  videoCredits,
}: {
  plan: PlanTier;
  planLimits: (typeof PLANS)[PlanTier];
  videoCredits: number;
}) {
  const portal = useMutation({
    mutationFn: () => billingApi.portal(),
    onSuccess: (data) => { window.location.href = data.url; },
  });

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-warning/15 text-warning">
            <CreditCard className="h-5 w-5" />
          </div>
          <div>
            <CardTitle>Plan & Credits</CardTitle>
            <CardDescription>Your subscription and usage</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <div className="rounded-lg border border-border bg-surface2/30 p-4 text-center">
            <p className="text-3xl font-bold text-fg">{videoCredits}</p>
            <p className="mt-1 text-sm text-fgMuted">Video credits</p>
          </div>
          <div className="rounded-lg border border-border bg-surface2/30 p-4 text-center">
            <p className="text-3xl font-bold text-fg">{planLimits.videosPerMonth}</p>
            <p className="mt-1 text-sm text-fgMuted">Videos/month</p>
          </div>
          <div className="rounded-lg border border-border bg-surface2/30 p-4 text-center">
            <p className="text-3xl font-bold text-fg capitalize">{planLimits.imageModel.replace("-", " ")}</p>
            <p className="mt-1 text-sm text-fgMuted">Image model</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button variant="outline" loading={portal.isPending} onClick={() => portal.mutate()}>
            <ExternalLink className="h-4 w-4" />
            Manage subscription
          </Button>
          <a href="/billing">
            <Button variant="ghost">View all plans</Button>
          </a>
        </div>
      </CardContent>
    </Card>
  );
}

function YouTubeSection({
  plan: _plan,
  planLimits,
}: {
  plan: PlanTier;
  planLimits: (typeof PLANS)[PlanTier];
}) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["youtube", "channels"],
    queryFn: () => channelsApi.list(),
    // (audit 10-17 B-F4) a connect the platform's sign-in cut off from this tab (its popup can't report back) is seen
    //  when the person returns here
    refetchOnWindowFocus: true,
  });

  // (tf3-34) the package's connect, in a popup, as TikTok's: the popup's page completes it and reports here. Only the
  //  connect THIS section started is reported here (the TikTok section's is its own).
  const started = useRef(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (!started.current || e.origin !== window.location.origin || e.data?.type !== CONNECT_MESSAGE) return;
      const r = e.data.result as ConnectResult;
      setConnectError(r.error ? connectErrorText(r.error) : null);
      void queryClient.invalidateQueries({ queryKey: ["youtube", "channels"] });
    };
    window.addEventListener("message", onMsg);
    // (audit 10-17 B-F3) a YouTube connect that finished in this window (the popup was blocked): its result is shown here
    const kept = takeKeptConnectResult("youtube");
    if (kept) setConnectError(kept.error ? connectErrorText(kept.error) : null);
    return () => window.removeEventListener("message", onMsg);
  }, [queryClient]);
  // called straight from the click (a popup opened outside the click is blocked), as TikTok's
  const connectYouTube = () => {
    setConnectError(null); setConnecting(true); started.current = true;
    channelsApi.connect()
      .catch((e: Error) => setConnectError(e.message || "The connection could not be started."))  // catch-reason: the error is shown to the person (the connect error)
      .finally(() => { setConnecting(false); started.current = false; void queryClient.invalidateQueries({ queryKey: ["youtube", "channels"] }); });
  };

  const disconnectChannel = useMutation({
    mutationFn: (id: string) => channelsApi.disconnect(id),
    onSuccess: (r) => {
      setDisconnected(disconnectWords("YouTube", r));   // (tf3-34b) what the Disconnect did
      queryClient.invalidateQueries({ queryKey: ["youtube", "channels"] });
    },
  });
  const [disconnected, setDisconnected] = useState<string | null>(null);

  const channels = data?.channels ?? [];
  const canAddMore = channels.length < planLimits.maxConnectedChannels;

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-danger/15 text-danger">
            <Youtube className="h-5 w-5" />
          </div>
          <div>
            <CardTitle>YouTube Channels</CardTitle>
            <CardDescription>
              Connect channels for direct publishing ({channels.length}/{planLimits.maxConnectedChannels} slots used)
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!planLimits.allowYoutubePublish ? (
          <div className="rounded-lg border border-border bg-surface2/30 p-6 text-center">
            <p className="text-fgMuted">YouTube publishing is available on Pro and Agency plans.</p>
            <a href="/billing" className="mt-3 inline-block">
              <Button variant="outline" size="sm">Upgrade plan</Button>
            </a>
          </div>
        ) : isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : (
          <div className="flex flex-col gap-3">
            {channels.map((ch) => (
              <div
                key={ch.id}
                className="flex items-center justify-between rounded-lg border border-border bg-surface2/30 px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <Youtube className="h-5 w-5 text-danger" />
                  <div>
                    <p className="text-sm font-medium text-fg">{ch.channelTitle ?? ch.youtubeChannelId}</p>
                    <p className="text-xs text-fgMuted">Connected {new Date(ch.connectedAt).toLocaleDateString()}</p>
                    <ReconnectLine ch={ch} onReconnect={connectYouTube} busy={connecting} />
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  loading={disconnectChannel.isPending && disconnectChannel.variables === ch.id}
                  onClick={() => {
                    if (confirm("Disconnect this YouTube channel? You can reconnect later.")) {
                      disconnectChannel.mutate(ch.id);
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4 text-danger" />
                </Button>
              </div>
            ))}

            {channels.length === 0 && (
              <p className="py-4 text-center text-sm text-fgMuted">No YouTube channels connected yet.</p>
            )}

            {canAddMore && (
              <Button
                variant="outline"
                loading={connecting}
                onClick={connectYouTube}
                className="mt-2"
              >
                <Plus className="h-4 w-4" />
                Connect YouTube channel
              </Button>
            )}
            {connectError && <p className="text-sm text-danger">{connectError}</p>}
            {disconnected && <p className="text-sm text-fgMuted" role="status">{disconnected}</p>}
            <YouTubeTerms />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TikTokSection({
  plan: _plan,
  planLimits,
}: {
  plan: PlanTier;
  planLimits: (typeof PLANS)[PlanTier];
}) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["tiktok", "channels"],
    queryFn: () => tiktokApi.list(),
    // (audit 10-17 B-F4) a connect the platform's sign-in cut off from this tab (its popup can't report back) is seen
    //  when the person returns here
    refetchOnWindowFocus: true,
  });

  // (tf3-26a) the package's connect, in a popup; the popup's own page completes it (connect/complete) and reports here
  const [connectError, setConnectError] = useState<string | null>(null);
  // (tf3-34) YouTube connects through the same popup now: only the connect THIS section started is reported here
  const started = useRef(false);
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (!started.current || e.origin !== window.location.origin || e.data?.type !== CONNECT_MESSAGE) return;
      const r = e.data.result as ConnectResult;
      setConnectError(r.error ? connectErrorText(r.error) : null);
      void queryClient.invalidateQueries({ queryKey: ["tiktok", "channels"] });
    };
    window.addEventListener("message", onMsg);
    // a connect that finished in this window (no popup) left its result for the page: TikTok's, here
    const kept = takeKeptConnectResult("tiktok");
    if (kept) setConnectError(kept.error ? connectErrorText(kept.error) : null);
    return () => window.removeEventListener("message", onMsg);
  }, [queryClient]);
  // (audit 10-15) called straight from the click, not through useMutation: a mutation runs its function after awaits
  //  (and pauses offline), and a popup opened outside the click is blocked
  const [connecting, setConnecting] = useState(false);
  const connectTikTok = () => {
    setConnectError(null); setConnecting(true); started.current = true;
    tiktokApi.connect()
      .catch((e: Error) => setConnectError(e.message || "The connection could not be started."))  // catch-reason: the error is shown to the person (the connect error)
      .finally(() => { setConnecting(false); started.current = false; void queryClient.invalidateQueries({ queryKey: ["tiktok", "channels"] }); });
  };

  const disconnectChannel = useMutation({
    mutationFn: (id: string) => tiktokApi.disconnect(id),
    onSuccess: (r) => {
      setDisconnected(disconnectWords("TikTok", r));   // (tf3-34b) what the Disconnect did
      queryClient.invalidateQueries({ queryKey: ["tiktok", "channels"] });
    },
  });
  const [disconnected, setDisconnected] = useState<string | null>(null);

  const channels = data?.channels ?? [];

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#00f2ea]/15 text-[#00f2ea]">
            <Music2 className="h-5 w-5" />
          </div>
          <div>
            <CardTitle>TikTok Accounts</CardTitle>
            <CardDescription>
              Connect TikTok accounts for direct publishing
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!planLimits.allowYoutubePublish ? (
          <div className="rounded-lg border border-border bg-surface2/30 p-6 text-center">
            <p className="text-fgMuted">TikTok publishing is available on Pro and Agency plans.</p>
            <a href="/billing" className="mt-3 inline-block">
              <Button variant="outline" size="sm">Upgrade plan</Button>
            </a>
          </div>
        ) : isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : (
          <div className="flex flex-col gap-3">
            {channels.map((ch) => (
              <div
                key={ch.id}
                className="flex items-center justify-between rounded-lg border border-border bg-surface2/30 px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <Music2 className="h-5 w-5 text-[#00f2ea]" />
                  <div>
                    <p className="text-sm font-medium text-fg">{ch.displayName ?? ch.tiktokOpenId}</p>
                    <p className="text-xs text-fgMuted">Connected {new Date(ch.connectedAt).toLocaleDateString()}</p>
                    <ReconnectLine ch={ch} onReconnect={connectTikTok} busy={connecting} />
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  loading={disconnectChannel.isPending && disconnectChannel.variables === ch.id}
                  onClick={() => {
                    if (confirm("Disconnect this TikTok account? You can reconnect later.")) {
                      disconnectChannel.mutate(ch.id);
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4 text-danger" />
                </Button>
              </div>
            ))}

            {channels.length === 0 && (
              <p className="py-4 text-center text-sm text-fgMuted">No TikTok accounts connected yet.</p>
            )}

            {connectError && <p className="text-sm text-danger" role="alert">{connectError}</p>}
            {disconnected && <p className="text-sm text-fgMuted" role="status">{disconnected}</p>}
            <Button
              variant="outline"
              loading={connecting}
              onClick={connectTikTok}
              className="mt-2"
            >
              <Plus className="h-4 w-4" />
              Connect TikTok account
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** (tf3-34b) a channel that needs its owner to connect again: the reason, and Reconnect (the same connect) */
function ReconnectLine({ ch, onReconnect, busy }: { ch: { status?: string; statusReason?: string | null }; onReconnect: () => void; busy: boolean }) {
  const prompt = reconnectPrompt(ch);
  if (!prompt) return null;
  return (
    <p className="mt-1 text-xs text-warning" role="status">
      {prompt}{" "}
      <button type="button" className="underline" disabled={busy} onClick={onReconnect}>Reconnect</button>
    </p>
  );
}

/** (tf3-34b; Part B) Instagram professional accounts, on Instagram Login (no Facebook Page), as TikTok's section */
function InstagramSection({ planLimits }: { planLimits: (typeof PLANS)[PlanTier] }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["instagram", "channels"], queryFn: () => instagramApi.list(), refetchOnWindowFocus: true });
  const [connectError, setConnectError] = useState<string | null>(null);
  const started = useRef(false);
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (!started.current || e.origin !== window.location.origin || e.data?.type !== CONNECT_MESSAGE) return;
      const r = e.data.result as ConnectResult;
      setConnectError(r.error ? connectErrorText(r.error) : null);
      void queryClient.invalidateQueries({ queryKey: ["instagram", "channels"] });
    };
    window.addEventListener("message", onMsg);
    const kept = takeKeptConnectResult("instagram");
    if (kept) setConnectError(kept.error ? connectErrorText(kept.error) : null);
    return () => window.removeEventListener("message", onMsg);
  }, [queryClient]);
  const [connecting, setConnecting] = useState(false);
  // called straight from the click (a popup opened outside the click is blocked)
  const connectInstagram = () => {
    setConnectError(null); setConnecting(true); started.current = true;
    instagramApi.connect()
      .catch((e: Error) => setConnectError(e.message || "The connection could not be started."))  // catch-reason: the error is shown to the person (the connect error)
      .finally(() => { setConnecting(false); started.current = false; void queryClient.invalidateQueries({ queryKey: ["instagram", "channels"] }); });
  };
  const [disconnected, setDisconnected] = useState<string | null>(null);
  const disconnectChannel = useMutation({
    mutationFn: (id: string) => instagramApi.disconnect(id),
    onSuccess: (r) => { setDisconnected(disconnectWords("Instagram", r)); queryClient.invalidateQueries({ queryKey: ["instagram", "channels"] }); },
  });
  const channels = data?.channels ?? [];
  return (
    <Card className="mb-6">
      <CardHeader>
        <div>
          <CardTitle>Instagram Accounts</CardTitle>
          <CardDescription>Connect an Instagram professional account (business or creator) to post Reels. No Facebook Page is needed.</CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        {!planLimits.allowYoutubePublish ? (
          <div className="rounded-lg border border-border bg-surface2/30 p-6 text-center">
            <p className="text-fgMuted">Instagram publishing is available on Pro and Agency plans.</p>
            <a href="/billing" className="mt-3 inline-block"><Button variant="outline" size="sm">Upgrade plan</Button></a>
          </div>
        ) : isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : (
          <div className="flex flex-col gap-3">
            {channels.map((ch) => (
              <div key={ch.id} className="flex items-center justify-between rounded-lg border border-border bg-surface2/30 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-fg">{ch.username ? `@${ch.username}` : ch.instagramUserId}</p>
                  <p className="text-xs text-fgMuted">Connected {new Date(ch.connectedAt).toLocaleDateString()}</p>
                  <ReconnectLine ch={ch} onReconnect={connectInstagram} busy={connecting} />
                </div>
                <Button variant="ghost" size="sm" aria-label="Disconnect this Instagram account" loading={disconnectChannel.isPending && disconnectChannel.variables === ch.id}
                  onClick={() => { if (confirm("Disconnect this Instagram account? You can reconnect later.")) disconnectChannel.mutate(ch.id); }}>
                  <Trash2 className="h-4 w-4 text-danger" />
                </Button>
              </div>
            ))}
            {channels.length === 0 && <p className="py-4 text-center text-sm text-fgMuted">No Instagram accounts connected yet.</p>}
            {connectError && <p className="text-sm text-danger" role="alert">{connectError}</p>}
            {disconnected && <p className="text-sm text-fgMuted" role="status">{disconnected}</p>}
            <Button variant="outline" loading={connecting} onClick={connectInstagram} className="mt-2">
              <Plus className="h-4 w-4" />
              Connect Instagram account
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface TeamMember {
  id: string;
  email: string;
  joinedAt: string;
}

interface TeamInvite {
  id: string;
  email: string;
  createdAt: string;
  expiresAt: string;
}

interface TeamData {
  owner: { id: string; email: string } | null;
  members: TeamMember[];
  pendingInvites: TeamInvite[];
  maxSeats: number;
  usedSeats: number;
  isOwner: boolean;
}

function TeamSection() {
  const queryClient = useQueryClient();
  const [inviteEmail, setInviteEmail] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["team", "members"],
    queryFn: () => apiGet<TeamData>("/team/members"),
  });

  const invite = useMutation({
    mutationFn: () => apiPost<{ ok: boolean }>("/team/invite", { email: inviteEmail }),
    onSuccess: () => {
      setInviteEmail("");
      queryClient.invalidateQueries({ queryKey: ["team"] });
    },
  });

  const removeMember = useMutation({
    mutationFn: (id: string) => apiDelete<{ ok: boolean }>(`/team/members/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["team"] }),
  });

  const cancelInvite = useMutation({
    mutationFn: (id: string) => apiDelete<{ ok: boolean }>(`/team/invite/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["team"] }),
  });

  const leave = useMutation({
    mutationFn: () => apiPost<{ ok: boolean }>("/team/leave"),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["team"] }),
  });

  if (isLoading || !data) {
    return (
      <Card className="mb-6">
        <CardContent className="flex justify-center py-8"><Spinner /></CardContent>
      </Card>
    );
  }

  // (audit 10-20 B5) any of the team's refusals in words (a remove-member 403 was swallowed)
  const teamError = (invite.error ?? removeMember.error ?? cancelInvite.error ?? leave.error) as { message?: string } | null;

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-500/10">
            <Users className="h-5 w-5 text-brand-500" />
          </div>
          <div>
            <CardTitle>Team</CardTitle>
            <CardDescription>
              {data.usedSeats} of {data.maxSeats} seats used
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Owner */}
        {data.owner && (
          <div className="mb-4 flex items-center justify-between rounded-lg border border-border px-4 py-3">
            <div className="flex items-center gap-3">
              <Shield className="h-4 w-4 text-brand-500" />
              <div>
                <p className="text-sm font-medium text-fg">{data.owner.email}</p>
                <p className="text-xs text-fgMuted">Owner</p>
              </div>
            </div>
          </div>
        )}

        {/* Members */}
        {data.members.map((m) => (
          <div key={m.id} className="mb-2 flex items-center justify-between rounded-lg border border-border px-4 py-3">
            <div className="flex items-center gap-3">
              <User className="h-4 w-4 text-fgMuted" />
              <div>
                <p className="text-sm font-medium text-fg">{m.email}</p>
                <p className="text-xs text-fgMuted">Joined {new Date(m.joinedAt).toLocaleDateString()}</p>
              </div>
            </div>
            {data.isOwner && (
              <Button
                variant="outline"
                size="sm"
                aria-label="Remove this member"
                onClick={() => removeMember.mutate(m.id)}
                loading={removeMember.isPending}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        ))}

        {/* Pending invites */}
        {data.pendingInvites.map((inv) => (
          <div key={inv.id} className="mb-2 flex items-center justify-between rounded-lg border border-dashed border-border px-4 py-3">
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-fgMuted" />
              <div>
                <p className="text-sm text-fgMuted">{inv.email}</p>
                <p className="text-xs text-fgMuted">
                  Pending · Expires {new Date(inv.expiresAt).toLocaleDateString()}
                </p>
              </div>
            </div>
            {data.isOwner && (
              <Button
                variant="outline"
                size="sm"
                aria-label="Cancel this invite"
                onClick={() => cancelInvite.mutate(inv.id)}
                loading={cancelInvite.isPending}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        ))}

        {/* Invite form (owner only) */}
        {data.isOwner && data.usedSeats < data.maxSeats && (
          <div className="mt-4 flex gap-2">
            <Input
              placeholder="team@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="flex-1"
            />
            <Button
              onClick={() => invite.mutate()}
              disabled={!inviteEmail.trim()}
              loading={invite.isPending}
            >
              <Plus className="h-4 w-4" />
              Invite
            </Button>
          </div>
        )}
        {teamError && <p className="mt-2 text-sm text-danger">{teamError.message}</p>}

        {/* Leave team (member only) */}
        {!data.isOwner && (
          <div className="mt-4">
            <Button
              variant="outline"
              onClick={() => leave.mutate()}
              loading={leave.isPending}
            >
              Leave team
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DangerSection({ onLogout }: { onLogout: () => void }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");   // (audit 10-20) the route requires it
  const [resendStatus, setResendStatus] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const { user } = useAuth();
  const navigate = useNavigate();

  const deleteAccount = useMutation({
    mutationFn: async () => {
      const { authApi } = await import("@/api/auth.ts");
      return authApi.deleteAccount(deletePassword);
    },
    onSuccess: () => {
      onLogout();
    },
    onError: () => {
      setConfirmDelete(false);
      setDeletePassword("");   // never kept after a refusal
    },
  });

  const resendVerification = async () => {
    setResendStatus("sending");
    try {
      const { authApi } = await import("@/api/auth.ts");
      await authApi.resendVerification();
      setResendStatus("sent");
      navigate("/otp", { state: { email: user?.email, verify: true } });
    } catch {
      setResendStatus("failed");
      setTimeout(() => setResendStatus("idle"), 3000);
    }
  };

  return (
    <Card className="border-danger/30">
      <CardHeader>
        <CardTitle className="text-danger">Danger zone</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Email verification */}
        {user && !(user as Record<string, unknown>).emailVerified && (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-fg">Verify your email</p>
              <p className="text-xs text-fgMuted">Your email hasn't been verified yet.</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={resendVerification}
              disabled={resendStatus !== "idle"}
            >
              {resendStatus === "sending" ? <Spinner className="w-3.5 h-3.5" /> : resendStatus === "sent" ? "Sent!" : resendStatus === "failed" ? "Failed" : "Resend"}
            </Button>
          </div>
        )}

        {/* Sign out */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-fg">Sign out of your account</p>
            <p className="text-xs text-fgMuted">You can log back in anytime with your email and password.</p>
          </div>
          <Button variant="danger" size="sm" onClick={onLogout}>
            Sign out
          </Button>
        </div>

        {/* (tf3-34) Data and privacy */}
        <div>
          <p className="text-sm font-medium text-fg">Data and privacy</p>
          <p className="text-xs text-fgMuted">What TubeForge reads and keeps from each platform, and how to take it back: <a className="underline" href="/settings/privacy">Data and privacy</a>.</p>
        </div>

        {/* Delete account */}
        <div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-fg">Delete your account</p>
              <p className="text-xs text-fgMuted">Permanently delete your account and all data. This cannot be undone.</p>
            </div>
            {!confirmDelete ? (
              <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
                Delete account
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Input type="password" placeholder="Your password, to confirm" autoComplete="current-password" value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)} className="w-40" />
                <Button variant="outline" size="sm" onClick={() => { setConfirmDelete(false); setDeletePassword(""); }}>Cancel</Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => deleteAccount.mutate()}
                  disabled={deleteAccount.isPending || !deletePassword}
                >
                  {deleteAccount.isPending ? <Spinner className="w-3.5 h-3.5" /> : "Confirm delete"}
                </Button>
              </div>
            )}
          </div>
          {deleteAccount.isError && (
            <p className="text-xs text-danger mt-2">
              {(deleteAccount.error as Error)?.message ?? "Account deletion failed. Please try again."}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/** (tf3-34; YouTube API Services Developer Policies III.A.1, III.A.2, III.D.2, read 10-17) the YouTube Terms of Service and
 *  Google's Privacy Policy, linked where a channel is connected, and how to revoke access. */
function YouTubeTerms() {
  return (
    <p className="mt-2 text-xs text-fgMuted">
      By connecting a channel you agree to the <a className="underline" href={YOUTUBE_TERMS_URL} target="_blank" rel="noreferrer">YouTube Terms of Service</a>.
      TubeForge uses YouTube API Services; see <a className="underline" href={GOOGLE_PRIVACY_URL} target="_blank" rel="noreferrer">Google's Privacy Policy</a> and{" "}
      <a className="underline" href="/settings/privacy">Data and privacy</a>. To revoke TubeForge's access, disconnect the channel here, or remove TubeForge
      on <a className="underline" href={GOOGLE_PERMISSIONS_URL} target="_blank" rel="noreferrer">Google's security settings page</a>.
    </p>
  );
}
